// The Knowledge bank item gone into (`/knowledge/{id}`): a source in its
// viewer (#89), or a raw file with "Make it a source". Each reads its own
// content through the ports (`usePortLoad`), with the explicit loading /
// failed states; a source no longer in the bank says so.

import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useCallback } from "react";
import { Button } from "../../../components/Button/Button.js";
import { EmptyState } from "../../../components/EmptyState/EmptyState.js";
import { Icon } from "../../../components/Icon/Icon.js";
import {
    fileKindOf,
    type KnowledgeRow,
} from "../../../components/KnowledgeBank/knowledgeBankModel.js";
import {
    elementFocusTarget,
    focusPagesOf,
} from "../../../components/Navigation/focusTarget.js";
import {
    cameFromStudio,
    isLeaveKey,
    leaveKeyOf,
} from "../../../components/Navigation/studioReturn.js";
import type {
    FocusTarget,
    ResourceRef,
} from "../../../components/Navigation/types.js";
import { SourceViewer } from "../../../components/SourceViewer/SourceViewer.js";
import { useAppShell } from "../context.js";
import { FileDetail, LoadView, usePortLoad } from "../ResourceDetail.js";
import { centeredStyle } from "../styles.js";

export const KnowledgeItem = ({
    id,
    row,
    target,
}: {
    readonly id: string;
    // The list row, once the Library has loaded (title, kind, reading state).
    readonly row: KnowledgeRow | undefined;
    readonly target: FocusTarget | undefined;
}): ReactNode => {
    const { t } = useLingui();
    const { ports, library, dispatch, promoteFileNow, frame } = useAppShell();
    const back = () => dispatch({ type: "open-knowledge" });
    // Opened on the Studio's canvas: back there (the crumb, and Esc).
    const toStudio = cameFromStudio(frame.history)
        ? () => dispatch({ type: "open-studio" })
        : undefined;
    // The Library has loaded and holds no such source: it was removed (or
    // the link is wrong). Say so, and lead back.
    if (row === undefined && library !== null)
        return (
            <div style={centeredStyle}>
                <EmptyState
                    icon={<Icon name="knowledge" size="lg" />}
                    title={t`This source is no longer in the knowledge bank`}
                    body={t`It was removed, or the link is wrong. Materials made from it keep what they say.`}
                >
                    <Button variant="primary" onClick={back}>
                        {t`Back to the knowledge bank`}
                    </Button>
                </EmptyState>
            </div>
        );
    if (row?.kind === "file")
        return (
            <FileDetail
                id={id}
                title={row.title}
                loadFile={ports.loadFile}
                onPromoteFile={promoteFileNow}
            />
        );
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: Esc back to the Studio, from anywhere in the viewer that does not own Esc itself.
        <div
            className="kb-item"
            onKeyDown={(e) => {
                if (toStudio === undefined || !isLeaveKey(leaveKeyOf(e)))
                    return;
                e.preventDefault();
                toStudio();
            }}
        >
            <SourceItem
                id={id}
                row={row}
                target={target}
                onBack={back}
                onBackToStudio={toStudio}
            />
        </div>
    );
};

const SourceItem = ({
    id,
    row,
    target,
    onBack,
    onBackToStudio,
}: {
    readonly id: string;
    readonly row: KnowledgeRow | undefined;
    readonly target: FocusTarget | undefined;
    readonly onBack: () => void;
    readonly onBackToStudio: (() => void) | undefined;
}): ReactNode => {
    const {
        ports,
        openRef,
        draftMaterialFromSource,
        requestUploadForSource,
        archiveResourceNow,
        unarchiveResourceNow,
        setConfirmDelete,
    } = useAppShell();
    const { load, retry } = usePortLoad(ports.loadSource, id);
    const { loadClaims } = ports;
    const readClaims = useCallback(
        (sourceId: string) =>
            loadClaims({ type: "claims_of_source", sourceId }),
        [loadClaims],
    );
    const claims = usePortLoad(readClaims, id);
    return (
        <LoadView
            load={load}
            kind="source"
            onRetry={retry}
            render={(source) => {
                const title = row?.title ?? source.meta.filename;
                const ref: ResourceRef = { type: "source", id, title };
                return (
                    <SourceViewer
                        id={id}
                        title={title}
                        fileKind={fileKindOf(
                            source.meta.filename,
                            source.original?.mediaType,
                        )}
                        source={source}
                        readStatus={row?.readStatus}
                        claims={
                            Remote.isLoaded(claims.load)
                                ? claims.load.value
                                : []
                        }
                        archived={row?.archived === true}
                        focusPages={focusPagesOf(target)}
                        onBack={onBack}
                        {...(onBackToStudio === undefined
                            ? {}
                            : { onBackToStudio })}
                        onOpenUse={(use) =>
                            openRef(use.material, elementFocusTarget(use.node))
                        }
                        onOpenMaterial={openRef}
                        onCreateMaterial={() => draftMaterialFromSource(ref)}
                        onArchive={() => archiveResourceNow(ref)}
                        onUnarchive={() => unarchiveResourceNow(ref)}
                        onRemove={() => setConfirmDelete(ref)}
                        onReupload={() =>
                            requestUploadForSource(id, title, {
                                onRestored: retry,
                            })
                        }
                    />
                );
            }}
        />
    );
};
